// useRowSelection.js
/**
 * useRowSelection — the ONE state model of a bulk selection (BS16, G0.9).
 *
 * @description
 *   Holds which rows a list has selected and hands back everything the two
 *   surfaces need: the tri-state header checkbox, the count for the bar, the
 *   Stufe-2 rule and the row gesture of the mobile selection mode (§11.6).
 *
 *   🔴 **Zwei Stufen, und die zweite ist eine REGEL, keine Liste.** Stufe 1
 *   (`modus: 'page'`) hält die Kennungen der geladenen Seite. Stufe 2
 *   (`modus: 'all'`) hält **„alle passenden außer …"** — eine Treffermenge von
 *   13 000 Zeilen als Liste von 13 000 Kennungen zu halten wäre ein
 *   Datenverlustpfad, keine Auswahl (§9.5). `count` ist dort `total − excluded`.
 *
 *   🔴 **Die Auswahl hängt an der Abfrage, nicht an der Zeit.** Ändert sich
 *   Suche, Filter, Reiter oder Sortierung, ist die Auswahl **leer** (§9.5,
 *   N10.3) — und zwar im **selben** Render, nicht einen Effekt später: eine
 *   geleerte Liste, die einen Frame lang noch „3 selected" sagt, ist genau der
 *   Datenverlustpfad, den die Regel schließt. Die Abfrage kommt deshalb nicht
 *   als zweiter Schlüssel herein, sondern **als `query`-Vertrag aus
 *   `useListQuery` (BS1)**: gelesen wird `query.state`, und `page`/`pageSize`
 *   fallen heraus — die Seite ist der Cursor, nicht die Abfrage (§9.5 nennt
 *   Suche, Filter, Reiter, Sortierung; ein Seitenwechsel behält die Auswahl).
 *
 *   🔴 **Der Modus endet mit der letzten Demarkierung** (§11.6): `active` ist
 *   keine eigene Zustandsvariable, sondern `count > 0`. Damit kann kein zweiter
 *   Zustand neben der Auswahl herlaufen und anders stehen als sie.
 *
 *   🔴 **Der Modus hat ZWEI Eintritte.** Der Langdruck (`gesteFuer`) ist an
 *   `(pointer: coarse)` gebunden; auf einem feinen Zeiger gibt es ihn nicht.
 *   Deshalb liefert der Hook zusätzlich `startSelection(key)` — derselbe
 *   idempotente Aufruf, aber **ohne Geste und auf jedem Zeiger**, für den
 *   Menüweg ⋮ → „Select" (§11.6, WCAG 2.5.1).
 *
 *   🔴 **Der Langdruck ist an `(pointer: coarse)` gebunden** — über
 *   `readCoarsePointer` aus `useSwipeRow` (**dieselbe** Prüfung, nicht eine
 *   zweite; auf einem Hybridgerät mit Trackpad gilt der Finger sonst als fein
 *   und die beiden Gesten laufen auseinander). Der Systemrand kommt aus
 *   `SWIPE_EDGE_GUARD_PX` (G3), die Drift aus `SWIPE_DIRECTION_LOCK_PX` (G4) —
 *   beide **vorhanden**, nicht neu definiert. `touch-action: pan-y` bleibt
 *   Sache der Wischfläche (G2); `navigator.vibrate` wird hier **nicht** gerufen
 *   (G12: Haptik ist Beiwerk, nie die einzige Rückmeldung).
 *
 *   🔴 **Kein `label`, kein Rückfallname.** Der Name der Kopf-Checkbox ist
 *   Pflicht und der Hook **wirft** ohne ihn (Hausform `EmptyState.reason`,
 *   `ResultCount.noun`, `MobileRecordCard.selectable.label`): in einer Liste mit
 *   fünfzig Zeilen hieße eine Rückfallbeschriftung fünfzigmal dasselbe.
 *
 * @useWhen
 *   - Eine Liste bietet Sammelaktionen an („Assign", „Export", „Delete") und
 *     braucht Auswahlzustand über mehrere Zeilen
 *   - Eine Tabelle braucht die dreistufige Kopf-Checkbox (§9.5)
 *   - Ein Kartenbestand soll mobil einen Auswahlmodus per Langdruck bekommen
 *     (§11.6) — dann zusätzlich `gesteFuer(key, onTap)` an die Karte streuen
 *
 * @dontUseFor
 *   - Eine Zeile mit einer einzigen Aktion — dafür kein Auswahlzustand
 *   - Ein Formular mit Mehrfachauswahl (Checkbox-Gruppe) — das ist kein
 *     Listenkontext und braucht keine Treffermengen-Regel
 *
 * @related
 *   - `BulkActionBar` (BS16) — die Fläche, die `count`, `modus` und die
 *     Kopf-Checkbox rendert
 *   - `useListQuery` (BS1) — die Quelle der `query`, an der die Auswahl hängt
 *   - `MobileRecordCard.selectable` (BS12) — die Checkbox der Karte
 *   - `useSwipeRow` (G7) — wird im Modus mit `enabled={false}` pausiert
 *
 * @reuseHints
 *   - Die Desktop-Tabelle trägt die Checkbox-Spalte (Spalte 1, §9.5); die
 *     Karte trägt im Ruhezustand **keine** Auswahlfläche, sondern die Geste
 *   - Ein zweiter Verbraucher mit anderer Treffermenge übergibt einfach ein
 *     anderes `total` — die Regel bleibt dieselbe
 *
 * @param {object} config
 * @param {object} config.query  Pflicht — der Vertrag aus `useListQuery` (BS1).
 *   Gelesen wird `query.state`; `page` und `pageSize` zählen nicht als
 *   Abfrageänderung. Fehlt `query`, wirft der Hook: ohne die Abfrage gäbe es
 *   keinen Grund, die Auswahl je zu leeren.
 * @param {Array<string|number>} [config.pageKeys] Die Kennungen der **geladenen
 *   Seite** — die Grundlage der dreistufigen Kopf-Checkbox (§9.5, Stufe 1).
 * @param {number} [config.total] Die **Treffermenge** M der aktuellen Abfrage.
 *   Fehlt sie (oder ist sie keine Zahl), gibt es Stufe 2 nicht —
 *   `canSelectAllMatching` ist dann `false` und die Leiste zeigt den Link nicht.
 * @param {string} config.headerLabel  Pflicht — der zugängliche Name der
 *   Kopf-Checkbox. **Wirft ohne ihn.**
 * @returns {{ count: number, total: number|null, modus: 'page'|'all', active: boolean,
 *   canSelectAllMatching: boolean, isSelected: Function, toggle: Function,
 *   select: Function, startSelection: Function, headerCheckbox: object,
 *   selectAllMatching: Function, selectPage: Function, clearPage: Function,
 *   clear: Function, gesteFuer: Function, selectedKeys: Array<string|number> }}
 *   `count` ist „N selected"; `active` ist der laufende Auswahlmodus
 *   (`count > 0`); `headerCheckbox` ist `{ checked, indeterminate, onChange,
 *   inputProps }` und **direkt an ein `Checkbox` streubar** — der zugängliche
 *   Name reist als `inputProps['aria-label']` mit (MUI kennt keine
 *   `label`-Prop, R16 B3); `select` ist das **idempotente** Hinzufügen (Pinsel,
 *   Programm), `startSelection` **derselbe** Aufruf als der **gestenfreie** Weg
 *   in den Modus (⋮ → „Select", WCAG 2.5.1, R16 C2), `toggle` das Umschalten
 *   (Tippen im Modus, Klick auf die Checkbox); `gesteFuer(key, onTap)` liefert
 *   `{ 'data-auswahl-key', onPointerDown, onPointerMove, onPointerEnter,
 *   onPointerUp, onPointerCancel, onContextMenu, onClick, style }` für **eine
 *   Karte** — das Kennungsmerkmal ist die Gegenstelle des Pinselstrichs
 *   (`AUSWAHL_KEY_ATTRIBUT`, R15 W1). `selectedKeys` ist die **vollständige**
 *   Auswahl über alle Seiten — die Menge, die eine Sammelaktion sendet, und
 *   damit dieselbe Menge, die `count` zählt (R15 B3); im `all`-Modus ist sie
 *   leer, weil die Treffermenge dort nicht aufzählbar ist. 🔴 **`onTap` ist der Klick des Aufrufers
 *   im Ruhezustand und Pflicht, sobald die Karte einen eigenen Weg hat**
 *   (G5b-Nachbesserung 1, R15 B1 / R16 B1): der zurückgegebene `onClick`
 *   schaltet im Modus um und ruft ausserhalb des Modus `onTap` — ein daneben
 *   gespreiztes eigenes `onClick` würde von diesem Spread überschrieben und der
 *   Tipp öffnete nichts mehr.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  readCoarsePointer,
  SWIPE_EDGE_GUARD_PX,
  SWIPE_DIRECTION_LOCK_PX,
} from '../../hooks/useSwipeRow'

/** Die Haltezeit des Langdrucks (§11.6: „≈500 ms, `pointerdown` + Halten"). */
export const LONG_PRESS_MS = 500

/**
 * 🔴 Die iOS-Eigenschaften beim Start der Geste (§11.6). Ohne sie öffnet iOS
 * beim Halten seine eigene Auswahl/Lupe, und die Geste erreicht die App nicht.
 * Als **Inline-`style`** zurückgegeben, nicht als `sx`: die Probe liest
 * `el.style` — an einer MUI-Klasse läse sie nichts (Regel 17 §3b).
 */
const GESTE_STIL = { WebkitTouchCallout: 'none', userSelect: 'none' }

/**
 * 🔴 Das Kennungsmerkmal, das `gesteFuer(key)` an die Karte schreibt — die
 * Gegenstelle des Pinselstrichs (R15 W1).
 *
 * **Warum es das braucht.** Auf einem Touchgeraet setzt der Browser beim
 * `pointerdown` **implizites Pointer-Capture** auf das gedrueckte Element.
 * Solange es gilt, werden `pointerenter`/`pointerover` **nicht** an andere
 * Elemente gefeuert — die Trefferpruefung entfaellt. Der Finger, der von Karte A
 * auf Karte B wandert, erreicht Bs `onPointerEnter` dann **nie**, und der Pinsel
 * waere in seinem einzigen Kontext (`(pointer: coarse)`) tot. `pointermove`
 * dagegen kommt weiter an; die Karte unter dem Finger wird deshalb ueber
 * `document.elementFromPoint` gelesen, unabhaengig von Capture.
 *
 * 🔴 **Der Anlassfall ist entfallen** (2026-10-14): das genannte Vorbild sass im
 * Karussell-Streifen von `pages/secondLife/OpenCases.jsx` (dort **explizites**
 * `setPointerCapture` und der Klick). Die Datei ist am 2026-10-14 zur
 * gruppierten Liste umgebaut worden (`c4ba16d661`, OE-SL1) — Streifen, Ziehen
 * und Capture sind weg, der Zeiger greift nirgends mehr. Hier gilt weiter das
 * implizite Capture und der `enter`.
 */
export const AUSWAHL_KEY_ATTRIBUT = 'data-auswahl-key'

/** Ein geteiltes leeres Set — nie in place verändert, nur als Startwert. */
const LEER = new Set()

function leerStand(signatur) {
  return { signatur, modus: 'page', keys: LEER, excluded: LEER }
}

/**
 * Ein Wert, wie er in die Signatur geht. **Mehrfachwerte werden SORTIERT.**
 *
 * 🔴 Die Reihenfolge eines Filters ist keine Abfrageaenderung (G0.9-Nachzug,
 * R15 W4). `useListQuery` (BS1) serialisiert Listen als `liste.join(',')` und
 * liest sie als `roh.split(',')` — dieselbe Filter**menge** in anderer
 * Reihenfolge („open,closed" gegen „closed,open") ergaebe sonst eine andere
 * Signatur, und die Auswahl wuerde ueber einer unveraenderten Einschraenkung
 * geleert. Die Richtung waere konservativ (zu viel leeren statt Datenverlust),
 * aber sie ist falsch: der Nutzer hat nichts geaendert.
 *
 * Sortiert wird ueber die **serialisierte** Form, nicht ueber die Rohwerte:
 * `Array.prototype.sort` vergleicht Zeichenketten, und ein Filterwert darf
 * jedes JSON sein (Zahl, Objekt, `null`).
 */
function stabil(wert) {
  if (!Array.isArray(wert)) return wert
  return wert.map((x) => JSON.stringify(x ?? null)).sort()
}

/**
 * Die Abfrage als Zeichenkette. `page`/`pageSize` fallen heraus: die Seite ist
 * der Cursor (§9.5 nennt Suche, Filter, Reiter, Sortierung), `pageSize` ist
 * Konfiguration. Sortierte Schlüssel, damit die Reihenfolge des `state`-Objekts
 * keine Rolle spielt — und sortierte MEHRFACHWERTE, damit die Reihenfolge
 * innerhalb eines Filters sie ebenfalls nicht spielt (`stabil`).
 */
function abfrageSignatur(query) {
  const zustand = query && query.state ? query.state : {}
  return Object.keys(zustand)
    .filter((k) => k !== 'page' && k !== 'pageSize')
    .sort()
    .map((k) => `${k}=${JSON.stringify(stabil(zustand[k] ?? null))}`)
    .join('&')
}

export default function useRowSelection({ query, pageKeys = [], total, headerLabel }) {
  if (!query || typeof query !== 'object') {
    throw new Error(
      'useRowSelection: `query` fehlt — es ist der Vertrag aus `useListQuery` (BS1). ' +
        'Ohne die Abfrage gäbe es keinen Grund, die Auswahl je zu leeren (§9.5).',
    )
  }
  // Hausform wie `EmptyState.reason`, `ResultCount.noun`, `RefreshButton.ariaLabel`:
  // der Name ist PFLICHT und der Baustein wirft ohne ihn, statt still einen
  // Platzhalter zu setzen.
  if (!headerLabel) {
    throw new Error(
      'useRowSelection: `headerLabel` fehlt — der zugängliche Name der Kopf-Checkbox ' +
        '(z. B. "Select all rows on this page"). Ein Rückfallname hieße in jeder Liste gleich.',
    )
  }

  const signatur = abfrageSignatur(query)

  const [stand, setStand] = useState(() => leerStand(signatur))

  // 🔴 Die Abfrage hat gewechselt: die Auswahl ist LEER — in DIESEM Render, nicht
  // erst nach einem Effekt. Das ist das Muster „Zustand beim Rendern anpassen"
  // (React-Doku, „Storing information from previous renders"): der Aufruf ist
  // durch den Vergleich geschützt und kann deshalb nicht schleifen. Ein Effekt
  // wäre einen Frame zu spät, und genau dieser Frame zeigt „3 selected" über
  // einer Liste, die die drei Zeilen nicht mehr enthält.
  if (stand.signatur !== signatur) setStand(leerStand(signatur))

  // 🔴 Nach demselben Wechsel kann ein ALTER Stand nicht zurückkommen: der neue
  // ist bereits geschrieben. Ein bloßes „beim Lesen verwerfen" ließe die Auswahl
  // auferstehen, sobald der Nutzer denselben Filter erneut setzt.

  const istGewaehlt = useCallback(
    (key) => (stand.modus === 'all' ? !stand.excluded.has(key) : stand.keys.has(key)),
    [stand],
  )

  const count = stand.modus === 'all'
    // 🔴 Der Boden ist 0: `excluded` kann nach einem Seitenwechsel Kennungen
    // tragen, die in der neuen Treffermenge nicht mehr vorkommen. „N selected"
    // darf nie negativ werden.
    ? Math.max(0, (Number.isFinite(total) ? total : 0) - stand.excluded.size)
    : stand.keys.size

  /**
   * ALLE gewaehlten Kennungen — die Grundlage eines Sammelaufrufs.
   *
   * 🔴 **Der Zaehler und die Wirkung muessen dieselbe Menge meinen** (R15 B3).
   * `count` zaehlt im `page`-Modus `stand.keys.size` über ALLE Seiten; wer die
   * Aktion auf `pageKeys ∩ selected` schneidet, zeigt „5 selected" und sendet
   * zwei — die drei anderen verschwinden ohne Wirkung und ohne Hinweis.
   *
   * Im `all`-Modus (`modus: 'all'`) ist die Treffermenge **nicht aufzaehlbar**
   * (sie kommt vom Server, §9.5 Stufe 2); dort bleibt die Liste leer, und der
   * Aufrufer muss die Seitenschluessel nehmen. Die Stufe 2 ist auf `/rma` nicht
   * gebaut (der Endpunkt nimmt Kennungen) — der Fall ist trotzdem benannt,
   * statt still eine halbe Menge zu senden.
   */
  const selectedKeys = useMemo(
    () => (stand.modus === 'all' ? [] : Array.from(stand.keys)),
    [stand],
  )

  const aufSeite = pageKeys.filter((k) => istGewaehlt(k))
  const seitenStand = pageKeys.length === 0 || aufSeite.length === 0
    ? 'none'
    : (aufSeite.length === pageKeys.length ? 'all' : 'some')

  const canSelectAllMatching = Number.isFinite(total) && total > pageKeys.length

  /**
   * Jede Aenderung laeuft ueber diesen Setzer: er schreibt IMMER die aktuelle
   * Signatur mit, damit ein alter Stand nicht wieder gelesen werden kann.
   */
  const aendere = useCallback((fn) => {
    setStand((prev) => {
      const basis = prev.signatur === signatur ? prev : leerStand(signatur)
      return fn(basis)
    })
  }, [signatur])

  const toggle = useCallback((key) => {
    aendere((basis) => {
      if (basis.modus === 'all') {
        const excluded = new Set(basis.excluded)
        if (excluded.has(key)) excluded.delete(key)
        else excluded.add(key)
        return { ...basis, excluded }
      }
      const keys = new Set(basis.keys)
      if (keys.has(key)) keys.delete(key)
      else keys.add(key)
      return { ...basis, keys }
    })
  }, [aendere])

  /** Idempotentes Hinzufuegen — Start des Modus und Pinselstrich. */
  const select = useCallback((key) => {
    aendere((basis) => {
      if (basis.modus === 'all') {
        if (!basis.excluded.has(key)) return basis
        const excluded = new Set(basis.excluded)
        excluded.delete(key)
        return { ...basis, excluded }
      }
      if (basis.keys.has(key)) return basis
      const keys = new Set(basis.keys)
      keys.add(key)
      return { ...basis, keys }
    })
  }, [aendere])

  /**
   * 🔴 **Der GESTENFREIE Weg in den Auswahlmodus — WCAG 2.5.1** (R16 C2).
   *
   * `gesteFuer` haengt an `(pointer: coarse)`: auf einem feinen Zeiger gibt es
   * den Modus damit **gar nicht**. Der Weg ueber das Zeilenmenue (⋮ → „Select")
   * ist der Ausweg, den §11.6 und T3 als den WCAG-2.5.1-Weg nennen — und er darf
   * keine Geste und keinen groben Zeiger verlangen.
   *
   * Dieselbe idempotente Markierung wie `select` (die Markierung ist der Start:
   * `active` ist `count > 0`, es gibt keinen zweiten Zustand daneben) — nur ohne
   * jede Zeigerbindung. Der Aufrufer setzt „Select" in sein `RowActionsMenu`
   * (BS12/`RowActionsMenu`) und ruft `startSelection(key)`.
   */
  const startSelection = select

  const selectPage = useCallback(() => {
    aendere((basis) => {
      if (basis.modus === 'all') {
        const excluded = new Set(basis.excluded)
        pageKeys.forEach((k) => excluded.delete(k))
        return { ...basis, excluded }
      }
      const keys = new Set(basis.keys)
      pageKeys.forEach((k) => keys.add(k))
      return { ...basis, keys }
    })
  }, [aendere, pageKeys])

  const clearPage = useCallback(() => {
    aendere((basis) => {
      if (basis.modus === 'all') {
        const excluded = new Set(basis.excluded)
        pageKeys.forEach((k) => excluded.add(k))
        return { ...basis, excluded }
      }
      const keys = new Set(basis.keys)
      pageKeys.forEach((k) => keys.delete(k))
      return { ...basis, keys }
    })
  }, [aendere, pageKeys])

  /**
   * Stufe 2 — „alle passenden ausser …", ohne die Seite zu verlassen.
   *
   * 🔴 **In Stufe 2 ist der Aufruf ein NO-OP** (R16 B2). Vorher setzte er
   * `excluded` auf leer und holte damit die vom Nutzer eben ABGEWÄHLTEN Zeilen
   * kommentarlos zurueck — der Link stand im Stufe-2-Zustand weiter sichtbar,
   * und ein Klick darauf machte aus 134 wieder 137. Stufe 2 leert `excluded`
   * nie still: nur `toggle` (Abwaehlen) und `clear` (Ende des Modus) fassen es
   * an.
   */
  const selectAllMatching = useCallback(() => {
    aendere((basis) => (
      basis.modus === 'all' ? basis : { ...basis, modus: 'all', keys: LEER, excluded: LEER }
    ))
  }, [aendere])

  /** „Clear selection" / „Cancel" — leert die Auswahl und beendet den Modus. */
  const clear = useCallback(() => {
    aendere((basis) => ({ ...basis, modus: 'page', keys: LEER, excluded: LEER }))
  }, [aendere])

  // 🔴 Der zugängliche Name reist als `inputProps['aria-label']` mit — nicht als
  // `label`. MUI `Checkbox` kennt **keine** `label`-Prop: beim Spread landete sie
  // als Attribut am Wurzelknoten und benannte die Checkbox NICHT (R16 B3). Die
  // Hausform ist `inputProps={{ 'aria-label': … }}` (z. B. `ProductDetail.jsx:2554` und rund
  // fünfzehn weitere Stellen); damit stimmt „direkt an ein `Checkbox` streubar".
  const headerCheckbox = {
    checked: seitenStand === 'all',
    indeterminate: seitenStand === 'some',
    onChange: () => (seitenStand === 'all' ? clearPage() : selectPage()),
    inputProps: { 'aria-label': headerLabel },
  }

  // ── Die Geste des Auswahlmodus (§11.6) ───────────────────────────────────
  //
  // 🔴 Refs statt Zustand: der Langdruck-Zeitgeber und der Pinsel muessen den
  // jeweils JUENGSTEN Rueckruf sehen; eine Schliessung ueber den Render-Stand
  // riefe nach 500 ms eine veraltete Auswahl.
  const laufRef = useRef(null)
  const pinselRef = useRef(false)
  const sichtbarRef = useRef(false)
  const verschluckeKlickRef = useRef(false)
  const [sichtbar, setSichtbar] = useState(false)

  const wahlRef = useRef({ select, toggle, active: false })
  wahlRef.current = { select, toggle, active: count > 0 }

  const beendeGeste = useCallback(() => {
    const lauf = laufRef.current
    if (lauf && lauf.timer) clearTimeout(lauf.timer)
    laufRef.current = null
    // 🔴 Ein Langdruck erzeugt auf dem Geraet noch ein `click`. Ohne diese
    // Marke schaltete es die eben markierte Karte sofort wieder ab.
    verschluckeKlickRef.current = pinselRef.current
    pinselRef.current = false
    sichtbarRef.current = false
    setSichtbar(false)
  }, [])

  const starteGeste = useCallback((e, key) => {
    // G1 — dieselbe Pruefung wie die Wischgeste, nicht eine zweite.
    if (!readCoarsePointer()) return
    if (e.button != null && e.button !== 0) return
    // G3 — der Systemrand gehoert dem Betriebssystem, nicht der Auswahl.
    const breite = typeof window !== 'undefined' ? window.innerWidth : 0
    if (breite > SWIPE_EDGE_GUARD_PX * 2) {
      if (e.clientX < SWIPE_EDGE_GUARD_PX) return
      if (e.clientX > breite - SWIPE_EDGE_GUARD_PX) return
    }
    verschluckeKlickRef.current = false
    const lauf = { x: e.clientX, y: e.clientY, gefeuert: false, timer: null }
    lauf.timer = setTimeout(() => {
      lauf.gefeuert = true
      pinselRef.current = true
      wahlRef.current.select(key)
    }, LONG_PRESS_MS)
    laufRef.current = lauf
    sichtbarRef.current = true
    setSichtbar(true)
  }, [])

  /**
   * 🔴 Der Pinselstrich liest die Karte UNTER DEM FINGER (R15 W1) — nicht das
   * Element, das ein `pointerenter` meldet. Auf Touch traegt der Browser das
   * implizite Pointer-Capture am `pointerdown`-Element; `pointerenter` an
   * Geschwisterkarten bleibt dann aus, waehrend `pointermove` weiter ankommt.
   *
   * Gelesen wird `document.elementFromPoint(clientX, clientY)` und daran das
   * Kennungsmerkmal `AUSWAHL_KEY_ATTRIBUT` — dasselbe, das `gesteFuer(key)`
   * mitliefert. Ohne Treffer passiert **nichts** (fail-safe: eine Karte, die das
   * Merkmal nicht weiterreicht, wird nicht markiert statt falsch markiert).
   */
  const pinsleUnterDemFinger = useCallback((e) => {
    if (!pinselRef.current) return
    if (typeof document === 'undefined' || typeof document.elementFromPoint !== 'function') return
    const el = document.elementFromPoint(e.clientX, e.clientY)
    const traeger = el && typeof el.closest === 'function'
      ? el.closest(`[${AUSWAHL_KEY_ATTRIBUT}]`)
      : null
    const kennung = traeger ? traeger.getAttribute(AUSWAHL_KEY_ATTRIBUT) : null
    if (kennung) wahlRef.current.select(kennung)
  }, [])

  const bewegeGeste = useCallback((e) => {
    const lauf = laufRef.current
    // Nach dem Feuern ist Bewegung der PINSEL, kein Abbruch (§11.6).
    if (!lauf || lauf.gefeuert) {
      pinsleUnterDemFinger(e)
      return
    }
    const drift = Math.max(Math.abs(e.clientX - lauf.x), Math.abs(e.clientY - lauf.y))
    if (drift > SWIPE_DIRECTION_LOCK_PX) {
      // Der Finger rollt — das ist kein Halten.
      clearTimeout(lauf.timer)
      laufRef.current = null
      sichtbarRef.current = false
      setSichtbar(false)
    }
  }, [pinsleUnterDemFinger])

  useEffect(() => () => {
    const lauf = laufRef.current
    if (lauf && lauf.timer) clearTimeout(lauf.timer)
  }, [])

  /**
   * Die Geste des Auswahlmodus — mit einem RÜCKFALL für den Ruhezustand.
   *
   * 🔴 **`onClick` im Ruhezustand ist PFLICHT, nicht Beiwerk** (R15 B1 / R16 B1).
   * Bis G5b stand am Aufrufer ein eigenes `onClick={handleCardClick}` NEBEN dem
   * Spread dieser Geste; JSX nimmt die letzte Eigenschaft, also gewann dieser
   * Handler — und der kehrte ohne `wahlRef.current.active` sofort zurück. Ein
   * Tipp auf eine Fallkarte öffnete damit NICHTS mehr.
   *
   * Der Handler nimmt den Kartenklick deshalb als Argument entgegen und ruft ihn
   * im Ruhezustand auf. Ein blosses Vertauschen der Spread-Reihenfolge wäre die
   * falsche Reparatur: dann navigierte ein Tipp im Auswahlmodus, statt
   * umzuschalten.
   */
  const gesteFuer = useCallback((key, onClick) => ({
    // 🔴 Das Kennungsmerkmal reist mit den Handlern (R15 W1): der Pinselstrich
    // findet die Karte darueber, auch wenn der Browser den Zeiger gefangen haelt.
    [AUSWAHL_KEY_ATTRIBUT]: key,
    onPointerDown: (e) => starteGeste(e, key),
    onPointerMove: bewegeGeste,
    // Der Pinselstrich: markiert weitere Karten, solange der Finger liegt.
    // 🔴 ZWEI Wege, und beide werden gebraucht: OHNE implizites Capture (Maus,
    // Stift, `pointerenter` kommt an) markiert der Eintritt, MIT Capture (Touch)
    // greift `bewegeGeste` ueber `elementFromPoint`. Beide rufen dasselbe
    // idempotente `select`, ein doppeltes Markieren ist also folgenlos.
    onPointerEnter: () => { if (pinselRef.current) wahlRef.current.select(key) },
    onPointerUp: beendeGeste,
    onPointerCancel: beendeGeste,
    onContextMenu: (e) => { if (sichtbarRef.current) e.preventDefault() },
    // 🔴 Im Modus schaltet ein Tipp UM statt die Karte zu oeffnen (§11.6) —
    // `preventDefault` nimmt dem Titel-Link die Navigation.
    onClick: (e) => {
      if (verschluckeKlickRef.current) {
        verschluckeKlickRef.current = false
        e.preventDefault()
        return
      }
      if (!wahlRef.current.active) {
        // Ruhezustand: der Aufrufer behaelt seinen Klick (Navigation).
        if (typeof onClick === 'function') onClick(e)
        return
      }
      e.preventDefault()
      wahlRef.current.toggle(key)
    },
    style: sichtbar ? GESTE_STIL : undefined,
    // 🔴 `sichtbar` gehoert in die Abhaengigkeiten: ohne sie bliebe der
    // memoisierte Rueckruf beim alten Wert stehen und die iOS-Eigenschaften
    // kaemen nie an der Karte an.
  }), [starteGeste, bewegeGeste, beendeGeste, sichtbar])

  return {
    count,
    // Die Treffermenge geht mit hinaus: die Leiste schreibt sie in „Select all
    // 137 matching" (§9.5) und soll sie nicht aus einer zweiten Quelle holen.
    total: Number.isFinite(total) ? total : null,
    modus: stand.modus,
    active: count > 0,
    canSelectAllMatching,
    isSelected: istGewaehlt,
    // Die vollstaendige Auswahl (alle Seiten) — s. `selectedKeys` oben.
    selectedKeys,
    toggle,
    select,
    // Der gestenfreie Weg in den Modus (⋮ → „Select", WCAG 2.5.1, R16 C2).
    startSelection,
    headerCheckbox,
    selectAllMatching,
    selectPage,
    clearPage,
    clear,
    gesteFuer,
  }
}
